import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { FaTimes } from "react-icons/fa";
import PrimaryButton from "../../components/common/PrimaryButton";
import SecondaryButton from "../../components/common/SecondaryButton";

const ROLES = ["Admin", "Developer"];
const STATUSES = ["Active", "Pending"];

const EMPTY_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  whatsAppNo: "",
  role: "Developer",
  status: "Active",
};

function validateEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function validatePhone(phone) {
  return /^\d{10}$/.test(phone);
}

export default function TeamMemberModal({
  show,
  mode = "add",
  member,
  onClose,
  onSave,
  onDelete,
  isSubmitting = false,
}) {
  const isEdit = mode === "edit";
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!show) return;
    if (isEdit && member) {
      setForm({
        firstName: member.firstName || "",
        lastName: member.lastName || "",
        email: member.email || "",
        whatsAppNo: member.whatsAppNo || "",
        role: member.role || "Developer",
        status: member.status,
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setErrors({});
  }, [show, isEdit, member]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validate = () => {
    const next = {};
    if (!form.firstName.trim()) next.firstName = "First name is required";
    if (!form.lastName.trim()) next.lastName = "Last name is required";
    if (!form.email.trim()) next.email = "Email is required";
    else if (!validateEmail(form.email)) next.email = "Enter a valid email address";
    if (!isEdit) {
      if (!form.whatsAppNo.trim()) next.whatsAppNo = "Phone number is required";
      else if (!validatePhone(form.whatsAppNo.trim())) {
        next.whatsAppNo = "Enter a valid 10-digit phone number";
      }
    }
    if (!form.role) next.role = "Role is required";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onSave({
      ...form,
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      whatsAppNo: form.whatsAppNo.trim(),
    });
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 10);
    handleChange("whatsAppNo", value);
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      dialogClassName="team-member-modal"
      contentClassName="team-member-modal-content"
    >
      <div className="team-member-modal-header">
        <div>
          <h2 className="team-member-modal-title">
            {isEdit ? "Edit Team Member" : "Add Team Member"}
          </h2>
          {!isEdit && (
            <p className="team-member-modal-subtitle">
              A temporary password will be generated and sent automatically.
            </p>
          )}
        </div>
        <button type="button" className="team-member-modal-close" onClick={onClose} aria-label="Close">
          <FaTimes />
        </button>
      </div>

      <div className="team-member-modal-body">
        <div className="team-member-form-row">
          <div className="team-member-field">
            <label className="team-member-label">
              First Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`team-member-input ${errors.firstName ? "is-invalid" : ""}`}
              placeholder="Ananya"
              value={form.firstName}
              onChange={(e) => handleChange("firstName", e.target.value)}
            />
            {errors.firstName && <div className="team-member-error">{errors.firstName}</div>}
          </div>
          <div className="team-member-field">
            <label className="team-member-label">
              Last Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`team-member-input ${errors.lastName ? "is-invalid" : ""}`}
              placeholder="Mehta"
              value={form.lastName}
              onChange={(e) => handleChange("lastName", e.target.value)}
            />
            {errors.lastName && <div className="team-member-error">{errors.lastName}</div>}
          </div>
        </div>

        <div className="team-member-field">
          <label className="team-member-label">
            Email Address <span className="text-danger">*</span>
          </label>
          <input
            type="email"
            className={`team-member-input ${errors.email ? "is-invalid" : ""}`}
            placeholder="ananya@company.com"
            value={form.email}
            onChange={(e) => handleChange("email", e.target.value)}
            readOnly={isEdit}
            disabled={isEdit}
          />
          {errors.email && <div className="team-member-error">{errors.email}</div>}
        </div>

        <div className="team-member-field">
          <label className="team-member-label">
            Phone Number {!isEdit && <span className="text-danger">*</span>}
          </label>
          <input
            type="tel"
            className={`team-member-input ${errors.whatsAppNo ? "is-invalid" : ""}`}
            placeholder="9876543210"
            value={form.whatsAppNo}
            onChange={handlePhoneChange}
            readOnly={isEdit}
            disabled={isEdit}
          />
          {errors.whatsAppNo && <div className="team-member-error">{errors.whatsAppNo}</div>}
        </div>

        <div className={`team-member-form-row ${isEdit ? "" : "team-member-form-row-single"}`}>
          <div className="team-member-field">
            <label className="team-member-label">
              Role <span className="text-danger">*</span>
            </label>
            <div className="team-member-select-wrap">
              <select
                className={`team-member-select ${errors.role ? "is-invalid" : ""}`}
                value={form.role}
                onChange={(e) => handleChange("role", e.target.value)}
              >
                {ROLES.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </select>
            </div>
            {errors.role && <div className="team-member-error">{errors.role}</div>}
          </div>

          {isEdit && (
            <div className="team-member-field">
              <label className="team-member-label">Status</label>
              <div className="team-member-select-wrap">
                <select
                  readOnly
                  disabled
                  className="team-member-select"
                  value={form.status}
                  onChange={(e) => handleChange("status", e.target.value)}
                >
                  {STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="team-member-modal-footer">
        {isEdit && onDelete ? (
          <button
            type="button"
            className="team-member-delete-btn"
            onClick={onDelete}
            disabled={isSubmitting}
          >
            Delete Member
          </button>
        ) : (
          <span />
        )}
        <div className="team-member-modal-footer-actions">
          <SecondaryButton onClick={onClose} disabled={isSubmitting}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting
              ? isEdit
                ? "Saving..."
                : "Adding..."
              : isEdit
                ? "Save Changes"
                : "Add Member"}
          </PrimaryButton>
        </div>
      </div>
    </Modal>
  );
}
